import { useMemo } from "react";

import { ItemIconChain, Tooltip } from "@similarweb/ui-components";
import { Box } from "@similarweb/ui-components/foundations";

import { NO_DATA_SIGN } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { cleanUrlForDisplay } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/urlUtils";

import { CitationLink } from "./PromptsComponent.styled";
import { RankedListTooltipContent } from "./RankedListTooltipContent";
import type { Citation, IRankedListTooltipRow } from "./types";

interface ICitationsCellProps {
    citations: Citation[];
}

export const CitationsCell = ({ citations }: ICitationsCellProps) => {
    const tooltipRows = useMemo<IRankedListTooltipRow[]>(
        () =>
            [...citations]
                .sort((a, b) => {
                    const diff = (b.influenceScore ?? 0) - (a.influenceScore ?? 0);
                    if (diff !== 0) {
                        return diff;
                    }

                    return (a.link ?? "").localeCompare(b.link ?? "");
                })
                .map((citation) => ({
                    leftValue: (
                        <CitationLink
                            href={citation.link}
                            target="_blank"
                            rel="noreferrer noopener"
                        >
                            {cleanUrlForDisplay(citation.link)}
                        </CitationLink>
                    ),
                    rightValue: ((citation.influenceScore ?? 0) * 100).toFixed(2),
                })),
        [citations],
    );

    return citations.length ? (
        <Tooltip
            placement="top"
            content={
                <RankedListTooltipContent
                    automationName="prompts-citations-tooltip"
                    leftHeader={translate("llm.prompts.column.citations.tooltip.header.url")}
                    rightHeader={translate("llm.prompts.column.citations.tooltip.header.influence_score")}
                    rows={tooltipRows}
                />
            }
        >
            <Box data-automation="prompts-citations-cell">
                <ItemIconChain
                    icons={citations.map((item) => item.icon)}
                    maxIconChain={4}
                />
            </Box>
        </Tooltip>
    ) : (
        NO_DATA_SIGN
    );
};
